/* eslint-disable no-use-before-define,no-undef */
import $ from 'jquery';
import _ from 'lodash-es';
import { axisBottom, axisLeft, axisRight, axisTop, tickSize, tickSizeInner, tickSizeOuter, tickFormat } from 'd3-axis';
import { min, max, sum, extent } from 'd3-array';
import { map, nest } from 'd3-collection';
import { format } from 'd3-format';
import { interpolate } from 'd3-interpolate';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select, selectAll, classed, attr, touches } from 'd3-selection';
import { line, area, arc, pie, innerRadius, outerRadius } from 'd3-shape';
import { timeDay, timeMonth, timeYear } from 'd3-time';
import { timeFormat, timeParse } from 'd3-time-format';
import { transition } from 'd3-transition';

// Trigger window resize event when the sidebar nav is expanded/collapsed so that charts are resized
function bindResize() {
    const $body = $('body');

    // Use regex to find if body contains stats or --stats class
    if (/(\bstats\b)/.test($body.attr('class'))) {
        // Create custom event MDN's 'old-fashioned way' for IE compat
        const navResize = document.createEvent('Event');
        navResize.initEvent('nav-resize', true, true);
        // Bind to click
        $('.pin').click(() => { window.dispatchEvent(navResize); });
        // Bind to animation events
        $body.on('webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend',
            () => { window.dispatchEvent(navResize); });
    }
}

// NOT EXPORTED
function checkViewport($element, callback, pixelOffset) {
    const $window = $(window);

    // Declare viewport position
    const windowHeight = $window.height();
    const windowTopPosition = $window.scrollTop();
    const windowBottomPosition = (windowTopPosition + windowHeight);

    // Declare element position
    const elementHeight = $element.outerHeight();
    const elementTopPosition = $element.offset().top;
    const elementBottomPosition = (elementTopPosition + elementHeight);
    let offset = pixelOffset;

    // If pixelOffset is set to render after element comes in view and it's taller than element,
    // Render element when it comes fully in viewport
    if (-offset > elementHeight) {
        offset = -elementHeight;
    }

    // Check if element is in viewport. Call callback and unbind this function if it is
    if ((elementBottomPosition + offset >= windowTopPosition) &&
        (elementTopPosition - offset <= windowBottomPosition)) {
        $window.off(`scroll.${$element[0].id} resize.${$element[0].id}`);
        callback();
    }
}

// Bind scroll and resize to render elements when they enter viewport
function renderInView(element, callback = element.animate, pixelOffset = 0) {
    /**
     * @param {Object} $element - jQuery instance to look for. If not jQuery instance,
     *      we'll assume it's a chart and coerce the `element` property to a jQuery instance
     * @param {function} callback=$element.animate - The callback function to call when
     *      the element is in view. Defaults to animate function for charts
     * @param {number} pixelOffset=0 - Number of pixels above element to start rendering,
     *      can be negative
     */

    const $window = $(window);
    let $element;

    // If element is not a jQuery object, we'll treat it as one of our charts and set defaults
    if ((element instanceof jQuery) || (element.jquery)) {
        $element = element;
    } else {
        $element = $(element.container);
    }

    if ($element.length > 0) {
        // Bind checkViewport function to scroll and resize
        $window.on(`scroll.${$element[0].id} resize.${$element[0].id}`, // events using proxy names
            _.throttle(() => { checkViewport($element, callback, pixelOffset); }, 200));

        // Check viewport at load
        $(() => { checkViewport($element, callback, pixelOffset); });
    }
}

/*
 * Helper functions for visualizations
 * NOT EXPORTED
 */
function getAxis(orientation, scale) {
    switch (orientation) {
        case 'top':
            return axisTop(scale);
        case 'bottom':
            return axisBottom(scale);
        case 'left':
            return axisLeft(scale);
        case 'right':
            return axisRight(scale);
        default:
            return false;
    }
}

function getTimeInterval(interval) {
    switch (interval) {
        case 'day':
            return timeDay;
        case 'month':
            return timeMonth;
        case 'year':
            return timeYear;
        default:
            return false;
    }
}

/*
 * Visualizations
 */

/**
 * Creates bar chart visualization with data source and options arguments.
 * @param {Object} dataSrc - Object of data to use for chart
 *
 * @param {string} align - Align the pie chart to the container 'left' or'center'
 * @param {number} breakPoint - Point at which the chart switches to mobile.
 * @param {string} containerSelector - Will be loaded into D3 selection.
 * @param {string[]} dataSelection - Array of strings specifying 'name' values to use in dataSrc.
 * @param {boolean} drawOverlayLabel - Whether to put a label on the thing
 * @param {boolean} enableLinks - Whether to add links to data
 * @param {boolean} fixedRadius - Chart will have fixed radius instead of responsive radius.
 * @param {number} heightRatio= - Ratio of height to width
 * @param {Object} margin - Specifies chart margins
 * @param {string} overlayLabel - Descriptor for overlay in center of pie
 * @param {string} overlayVariable - Name of variable key to use for overlay value
 * @param {number} pieInnerRadius - Turns a pie into a donut. Decimal from 0 to 1.
 * @param {boolean} tooltips - Whether to add tooltips
 * @param {number} transitionDelay - Milliseconds to delay between transitions
 * @param {number} transitionDuration - Milliseconds duration of animations
 */
function PieChart(dataSrc, {
    align = 'center',
    breakPoint = 768,
    containerSelector = 'body',
    dataSelection = dataSrc.map(item => item.name),
    drawOverlayLabel = false,
    enableLinks = false,
    fixedRadius = false,
    heightRatio = 0.4,
    margin = { top: 0, right: 0, bottom: 0, left: 0 },
    overlayLabel = 'Visible',
    overlayVariable = 'visible-records',
    pieInnerRadius = 0,
    tooltips = true,
    transitionDelay = 500,
    transitionDuration = 1500,
} = {}) {
    const container = select(containerSelector);
    const sourceData = dataSrc;
    const marginBottom = margin.bottom;
    const marginTop = margin.top;
    let marginLeft = margin.left;
    let marginRight = margin.right;
    let svg;
    let width = parseInt(container.style('width'), 10)
        - parseInt(container.style('padding-left'), 10)
        - parseInt(container.style('padding-right'), 10)
        || document.documentElement.clientWidth;
    let height = width * heightRatio;
    let radius = Math.min(width, height) / 2;

    let chartWrapper;
    let chartArc;
    let pieChart;
    let slices;

    let dataTotal = 0;
    let labelData = 0;

    // return early if no data is available
    if (dataSrc === undefined) return {};

    // Filter data to only include the items we want in the pie
    // Set the values of our total and label data while we're iterating
    const data = sourceData.filter(element => (
        dataSelection.find((arrElement) => {
            if (element.name === arrElement) {
                dataTotal += +element.value;
                if (element.name === overlayVariable) {
                    labelData = +element.value;
                }
                return true;
            } return false;
        })
    ));

    // Set percentage formatting and calculate percentage for label
    const formatPercent = format('.1%');
    // If labelPercent is 100% use no decimal
    const labelPercent = labelData / dataTotal === 1 ? '100%' : formatPercent(labelData / dataTotal);
    // format for numbers with commas
    const commaFormatter = format(',');

    // Construct the overlay label here, it won't change
    const fullOverlayLabel = `${labelPercent} ${overlayLabel}`;

    init();

    function init() {
        // initialize svg elements
        svg = container.append('svg')
            .classed('visualization__outer-svg', true);
        chartWrapper = svg
            .append('g')
            .datum(data)
            .classed('visualization__inner-g', true);
        chartArc = arc()
            .innerRadius(0);
        pieChart = pie()
            .value(d => d.value)
            .sort(null);

        // render the chart
        render();

        // add event listeners to resize chart when window or nav is resized
        window.addEventListener('nav-resize', resize);
        window.addEventListener('resize', resize);
    }

    function render() {
        // Set dimensions based on container size
        updateDimensions();

        // Draw chart elements
        drawSlices();
        if (drawOverlayLabel) {
            drawLabel();
        }
    }

    function animate() {
        // Animate chart on draw
        slices.append('path')
            .transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attrTween('d', arcTween);

        chartWrapper.select('.pie-chart__overlay').transition()
            .delay(transitionDelay + transitionDuration)
            .text(fullOverlayLabel);
    }

    function resize() {
        // Update elements without redrawing
        updateDimensions();
        resizeSlices();
    }

    function drawSlices() {
        // Draw slices of chart using pie path
        if (enableLinks) {
            slices = chartWrapper.selectAll('.pie-chart__slice')
                .data(pieChart)
                .enter()
                .append('a')
                .attr('class', d => `pie-chart__slice  pie-chart__slice--${d.data.name}`)
                .attr('id', d => `pie-chart-${d.data.name}`)
                .attr('xlink:href', d => d.data.url);
        } else {
            slices = chartWrapper.selectAll('.pie-chart__slice')
                .data(pieChart)
                .enter()
                .append('g')
                .attr('class', d => `pie-chart__slice  pie-chart__slice--${d.data.name}`);
        }

        if (tooltips) {
            slices.attr('data-toggle', 'touch-tooltip')
                .attr('data-html', 'true')
                .attr('data-placement', 'right auto')
                .attr('title', d => (
                    `${d.data.description}</br>${commaFormatter(d.data.value)} items</br>${formatPercent(d.data.value / dataTotal)}`
                ));
        }
    }

    function resizeSlices() {
        // Resize slices without redrawing
        slices.selectAll('path').attr('d', chartArc);
    }

    function drawLabel() {
        // Draw overlay label in middle of chart
        chartWrapper.append('text')
            .attr('text-anchor', 'middle')
            .classed('pie-chart__overlay', true);
    }

    function updateDimensions() {
        // Determine whether client is mobile width
        const mobile = window.innerWidth < breakPoint;

        // Container dimensions
        const containerWidth = $(container.node()).width();
        $(container.node()).height(containerWidth);
        const containerHeight = $(container.node()).height();

        // adjust margins if on a mobile device without mutating original margins
        marginRight = mobile ? 0 : margin.right;
        marginLeft = mobile ? 0 : margin.left;

        // adjust for margins, method depends on whether pie is responsive or fixed radius
        if (fixedRadius) {
            width = (radius * 2) - marginLeft - marginRight;
            height = (radius * 2) - marginTop - marginBottom;
        } else {
            width = containerWidth - marginLeft - marginRight;
            height = (containerWidth) - marginTop - marginBottom;
            radius = Math.min(width, height) / 2;
        }

        // update svg and wrapper to new dimensions
        svg.attr('width', width + marginLeft + marginRight)
            .attr('height', height + marginTop + marginBottom);

        switch (align) {
            case 'left':
                chartWrapper.attr('transform',
                    `translate(${radius + marginLeft},${radius + marginTop})`);
                break;
            case 'right':
                // Future you will have to write this before you can use it.
                break;
            case 'center':
            default:
                chartWrapper.attr('transform',
                    `translate(${(containerWidth / 2) + marginLeft},${(containerHeight / 2) + marginTop})`);
        }

        // calculate inner radius as percentage of outer radius
        const newInnerRadius = radius * pieInnerRadius;

        // set arc of pie chart using new radius
        chartArc = chartArc.outerRadius(radius)
            .innerRadius(newInnerRadius);
    }

    function arcTween(a) {
        // Custom tween function to interpolate for initial transition
        const interpolateArc = interpolate({ startAngle: 0, endAngle: 0 }, a);

        return function (t) {
            return chartArc(interpolateArc(t));
        };
    }

    // Return render function and container node (instead of container d3 object)
    return {
        render,
        animate,
        resize,
        container: container.node(),
    };
}

/**
 * Creates bar chart visualization with data source and options arguments.
 * @param {Object} dataSrc - Object of data to use for chart
 *
 * @param {number} breakPoint - Width in pixels at which the chart switches to mobile.
 * @param {string} chartLabel - Label added to figcaption below chart
 * @param {string} containerSelector - Will be loaded into D3 selection.
 * @param {number} heightRatio - Ratio of height to width
 * @param {Object} margin - Specifies chart margins
 * @param {string} tooltipLabel - Descriptor for tooltip
 * @param {boolean} tooltips - Whether to add tooltips
 * @param {number} transitionDelay - Milliseconds to delay between transitions
 * @param {number} transitionDuration - Milliseconds duration of animations
 */
function BarChart(dataSrc, {
    breakPoint = 768,
    chartLabel = '',
    containerSelector = 'body',
    heightRatio = 0.1,
    margin = { top: 13.5, right: 0, bottom: 13.5, left: 0 },
    tooltipLabel = 'items',
    tooltips = true,
    transitionDelay = 500,
    transitionDuration = 1500,
} = {}) {
    const container = select(containerSelector);
    const commaFormatter = format(',');
    const $figcaption = $(container.node()).siblings('figcaption');
    const formatPercent = format('.1%');
    const marginBottom = margin.bottom;
    const marginTop = margin.top;
    const sourceData = dataSrc.recentData[0];
    const itemsTotal = sourceData[sourceData.length - 1][1];
    let bars;
    let chartWrapper;
    let height;
    let marginLeft = margin.left;
    let marginRight = margin.right;
    let svg;
    let width = parseInt(container.style('width'), 10)
        - parseInt(container.style('padding-left'), 10)
        - parseInt(container.style('padding-right'), 10)
        || document.documentElement.clientWidth;
    let xAxis;
    let xScale;

    // return early if no data is available
    if (dataSrc.recentData === undefined) return {};

    // Manipulate the data for use by creating objects with newItems property
    // and coordinates
    const data = sourceData.filter((element, index, array) => (
        // Filter to remove any data points with no items added over previous point
        index === 0 ? (element[1] > 0) : (element[1] > array[index - 1][1])
    )).map((element, index, array) => ({
        // Map data to usable object and count new items added
        date: element[0],
        count: element[1],
        newItems: index === 0 ? element[1] : element[1] - array[index - 1][1],
        }),
    );

    // Group the counts by year
    const groupedData = nest()
        .key(d => new Date(d.date).getFullYear())
        .rollup(d => (
            sum(d, g => g.newItems)
        ))
        .entries(data);

    groupedData.forEach(function (element, index, array) {
        element.x0 = index === 0 ? 0 : array[index - 1].x1;
        element.x1 = element.x0 + +element.value;
        element.percentage = formatPercent(element.value / itemsTotal);
    });

    // Attempt to label the chart if a figcaption exists
    if ($figcaption) {
        $figcaption.text(chartLabel);
    }

    init();

    function init() {
        // set scale
        xScale = scaleLinear()
            .___domain([0, itemsTotal]);

        // set axis
        xAxis = getAxis('top', xScale);

        // initialize svg elements
        svg = container.append('svg')
            .classed('visualization__outer-svg', true);
        chartWrapper = svg
            .append('g')
            .classed('visualization__inner-g', true);
        bars = chartWrapper.selectAll('rect');

        // render the chart
        render();

        // add event listeners to resize chart when window or nav is resized
        window.addEventListener('nav-resize', resize);
        window.addEventListener('resize', resize);
    }

    function render() {
        // Set dimensions based on container size
        updateDimensions();

        // Draw the bars
        drawBars();
    }

    function animate() {
        bars.transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attr('x', d => xScale(d.x0))
            .attr('width', d => xScale(d.x1) - xScale(d.x0))
            .transition()
            .duration(transitionDuration)
            .attr('height', height)
            .attr('y', 0);
    }

    function resize() {
        updateDimensions();
        resizeBars();
    }

    function updateDimensions() {
        // Determine whether the client is mobile
        const mobile = window.innerWidth < breakPoint;

        const containerWidth = container.node().getBoundingClientRect().width;

        // adjust margins if we're on a mobile device without mutating original margins
        marginRight = mobile ? 0 : margin.right;
        marginLeft = mobile ? 0 : margin.left;

        // adjust for margins
        width = containerWidth - marginLeft - marginRight;
        height = heightRatio * width;

        // update scale range for new dimensions
        xScale.rangeRound([0, width], 1);

        // update x axes scale
        svg.select('.x.axis')
            .call(xAxis);

        // update svg and wrapper to new dimensions
        svg.attr('width', width + marginLeft + marginRight)
            .attr('height', height + marginTop + marginBottom);
        chartWrapper.attr('transform', `translate(${marginLeft},${marginTop})`);
    }

    function drawBars() {
        // Draw rectangles in center of chart prior to animation
        bars = bars.data(groupedData)
            .enter()
            .append('rect')
            .attr('class', d => `bar-chart__bar  bar-chart__bar--${d.key}`)
            .attr('x', () => width / 2)
            .attr('y', () => (height - 10) / 2)
            .attr('width', 0)
            .attr('height', 10)
            .attr('data-new-items', d => d.value)
            .attr('data-percentage', d => d.percentage);

        // Attach tooltip data
        if (tooltips) {
            bars.data(groupedData)
                .attr('data-toggle', 'touch-tooltip')
                .attr('data-html', 'true')
                .attr('data-placement', 'top auto')
                .attr('title', d => `${d.key}</br>${commaFormatter(d.value)} ${tooltipLabel}</br>${d.percentage}`);
        }
    }

    function resizeBars() {
        // Resize the bars for responsiveness
        bars.attr('x', d => xScale(d.x0))
            .attr('width', d => xScale(d.x1) - xScale(d.x0))
            .attr('height', height)
            .attr('y', 0);
    }

    // Return render function and container node (instead of container d3 object)
    return {
        render,
        animate,
        resize,
        container: container.node(),
    };
}

/**
 * Creates line chart visualization with data source and options arguments.
 * @param {Object} dataSrc - Object of data to use for chart
 * @param {array} dataSrc.recentData - [INT, INT] DTObject as INT and usage count
 * @param {boolean} dataSrc.tisYear - Whether the data is for a year
 * @param {string} dataSrc.goToUrl - Base directory of URI for accessing dates
 * @param {string} dataSrc.chartLabel - Label to add to figcaption below chart
 *
 * @param {number} breakPoint - Point at which the chart switches to mobile.
 * @param {string} chartLabel - See dataSrc.chartLabel above.
 * @param {string} containerSelector - Will be loaded into D3 selection.
 * @param {number} dotRadius - Pixel radius of dots on line chart.
 * @param {number} dotRadiusActive - Pixel radius of activated dots on line chart.
 * @param {string} goToUrl - See dataSrc.goToUrl above.
 * @param {number} heightRatio - Ratio of height to width
 * @param {Object} margin - Specifies chart margins
 * @param {boolean} shiftEmpties - Shift data array to remove empty values at beginning
 * @param {string} statsTable - ID of stats table to associate with chart
 * @param {string} tickDateFormat - Format of dates on ticks
 * @param {string} tooltipDateFormat - Format of dates used in tooltips
 * @param {string} tooltipLabel - Descriptor for y axis value of chart in tooltip(e.g.: 'Usage: 13')
 * @param {boolean} tooltips - Whether to add tooltips
 * @param {number} transitionDelay - Delay of transitions in milliseconds
 * @param {number} transitionDuration - Duration of transitions in milliseconds
 * @param {string} urlDateFormat - Format of dates used in URL links
 * @param {string} xAxisLabel - Label for x axis
 * @param {string} xAxisOrient - Orientation of x axis. ('top' 'bottom' 'left' 'right')
 * @param {string} xAxisOrientMobile - Orientation of x axis in mobile.
 *      ('top' 'bottom' 'left' 'right')
 * @param {string} xAxisTickIntervalString - Time interval string
 * @param {number} xAxisTickStep - Defines steps between x axis ticks
 * @param {string} yAxisLabel - Label for y axis
 * @param {string} yAxisOrient - Orientation of y axis. ('top' 'bottom' 'left' 'right')
 * @param {string} yAxisOrientMobile - Orientation of y axis in mobile.
 *      ('top' 'bottom' 'left' 'right')
 * @param {number} yAxisTickCount - Approximate number of y axis ticks.
 */
function LineChart(dataSrc, {
    breakPoint = 768,
    chartLabel = dataSrc.chartLabel,
    containerSelector = 'body',
    dotRadius = 3.5,
    dotRadiusActive = 6,
    goToUrl = dataSrc.goToUrl || '',
    heightRatio = 0.5,
    margin = { top: 10, right: 0, bottom: 50, left: 0 },
    shiftEmpties = false,
    statsTable = undefined,
    tickDateFormat = '%b %d',
    tooltipDateFormat = '%b %d',
    tooltipLabel = 'Uses',
    tooltips = true,
    transitionDelay = 500,
    transitionDuration = 1500,
    urlDateFormat = '%Y-%m-%d',
    xAxisLabel = undefined,
    xAxisOrient = 'bottom',
    xAxisOrientMobile = 'bottom',
    xAxisTickIntervalString = 'day',
    xAxisTickStep = 3,
    yAxisLabel = undefined,
    yAxisOrient = 'left',
    yAxisOrientMobile = 'right',
    yAxisTickCount = 5,
} = {}) {
    const commaFormatter = format(',');
    const container = select(containerSelector);
    const $figcaption = $(container.node()).siblings('figcaption');
    const marginBottom = margin.bottom;
    const marginTop = margin.top;
    const sourceData = dataSrc.recentData ? dataSrc.recentData[0] : [];
    const xAxisDateFormatter = timeFormat(tickDateFormat);
    const xAxisTickInterval = getTimeInterval(xAxisTickIntervalString);
    const urlDateFormatter = timeFormat(urlDateFormat);
    const tooltipDateFormatter = timeFormat(tooltipDateFormat);
    let chartArea;
    let chartWrapper;
    let circles;
    let dots;
    let height;
    let chartLine;
    let marginLeft = margin.left;
    let marginRight = margin.right;
    let path;
    let svg;
    let touchScale;
    let width = parseInt(container.style('width'), 10)
        - parseInt(container.style('padding-left'), 10)
        - parseInt(container.style('padding-right'), 10)
        || document.documentElement.clientWidth;
    let xAxis;
    let xScale;
    let yAxis;
    let yScale;
    let yTickFormatter;

    // return a placeholder if there is no data
    if (dataSrc.recentData === undefined) {
        return {
            animate() {
                $(container).html('<div class="alert  alert-warning">No data for the specified time period</div>');
            },
            container: container.node(),
        };
    }

    // map source data to new data object
    const data = sourceData.map(d => ({
        date: d[0],
        count: d[1],
    }));

    // Remove empty values at start of data
    if (shiftEmpties) {
        while (data[0].count === 0) {
            data.shift();
        }
    }

    // Zero counts for starter data, enabling transitions from zero
    const startData = data.map(d => ({
        date: d.date,
        count: 0,
    }));

    // Attempt to label the chart if a figcaption exists
    if ($figcaption) {
        $figcaption.text(chartLabel);
    }

    init();

    function init() {
        // initialize scales and set domains
        xScale = scaleTime()
            .___domain(extent(data, d => d.date));

        yScale = scaleLinear()
            .___domain([0, max(data, d => d.count * 1.10)]);

        // initialize axes
        xAxis = getAxis(xAxisOrient, xScale)
            .tickFormat(xAxisDateFormatter);

        yAxis = getAxis(yAxisOrient, yScale)
            .ticks(yAxisTickCount);

        // initialize svg elements
        svg = container.append('svg')
            .classed('visualization__outer-svg', true);
        chartWrapper = svg
            .append('g')
            .classed('visualization__inner-g', true);
        chartArea = area()
            .x(d => xScale(d.date))
            .y1(d => yScale(d.count));
        chartLine = line()
            .x(d => xScale(d.date))
            .y(d => yScale(d.count));
        path = chartWrapper.append('path')
            .classed('visualization__line', true);
        chartWrapper.append('g')
            .classed('x axis', true);
        chartWrapper.append('g')
            .classed('y axis', true);

        // bind touch-swipe functionality
        chartWrapper.on('touchmove', onTouchMove, { passive: true });

        // create a touch scale to find closest datapoint on touchmove
        touchScale = scaleLinear();

        // render the chart
        render();

        // add event listeners to resize chart when window or nav is resized
        window.addEventListener('nav-resize', resize);
        window.addEventListener('resize', resize);
    }

    function render() {
        // Call both axes before dimensions update to determine label width
        svg.select('.y.axis')
            .call(yAxis);

        svg.select('.x.axis')
            .call(xAxis);

        // Set dimensions based on container size
        updateDimensions();

        // add y axis with new dimensions
        svg.select('.y.axis')
            .call(yAxis);

        // add x axis
        svg.select('.x.axis')
            .attr('transform', `translate(0,${height})`)
            .call(xAxis)
            .selectAll('text')
            .style('text-anchor', 'middle')
            .attr('dx', '0')
            .attr('dy', '1em')
            .text(d => xAxisDateFormatter(new Date(d)));

        // add x axis label
        if (xAxisLabel) {
            chartWrapper.append('text')
                .classed('x label', true)
                .attr('text-anchor', 'end')
                .attr('x', width / 2)
                .attr('y', (height + marginBottom) - 30)
                .attr('dy', '.71em')
                .text(xAxisLabel);
        }

        // add y axis label
        if (yAxisLabel) {
            chartWrapper.append('text')
                .classed('y label', true)
                .attr('transform', 'rotate(-90)')
                .attr('y', '5')
                .attr('x', '0')
                .attr('dy', '.71em')
                .style('text-anchor', 'end')
                .text(yAxisLabel);
        }

        // add area below line
        chartWrapper.append('path')
            .datum(startData)
            .classed('visualization__area', true)
            .attr('d', chartArea);

        // add line with starter data
        path.datum(startData)
            .attr('d', chartLine);

        // add dots with anchor links
        dots = chartWrapper.selectAll('.visualization__circle--link')
            .data(startData)
            .enter()
            .append('a')
            .classed('visualization__circle--link', true)
            .attr('id', d => `${container.attr('id')}-${urlDateFormatter(new Date(d.date))}`)
            .attr('xlink:href', d => goToUrl + urlDateFormatter(new Date(d.date)));

        // add svg circles at starter data positions
        circles = dots.append('circle')
            .classed('visualization__circle', true)
            .attr('r', dotRadius)
            .attr('cx', d => xScale(d.date))
            .attr('cy', d => yScale(d.count));

        // highlight value in table on dot hover
        $(circles.nodes()).on('mouseover', function () {
            $(this).attr('r', dotRadiusActive);
            const month = $(this).attr('data-month');
            const year = $(this).attr('data-year');
            $(statsTable).find(`[data-year="${year}"][data-month="${month}"]`)
                .addClass('success');
        }).on('mouseout', function () {
            $(this).attr('r', dotRadius);
            const month = $(this).attr('data-month');
            const year = $(this).attr('data-year');
            $(statsTable).find(`[data-year="${year}"][data-month="${month}"]`)
                .removeClass('success');
        });
    }

    function animate() {
        // bind actual data, add tooltips, transition into position
        circles.data(data)
            .attr('data-month', d => (new Date(d.date).getMonth() + 1))
            .attr('data-year', d => new Date(d.date).getFullYear());

        // Attach data and initialize bootstrap tooltip API
        if (tooltips) {
            circles.attr('data-toggle', 'touch-tooltip')
                .attr('data-html', 'true')
                .attr('data-placement', 'top')
                .attr('title', d => `${tooltipDateFormatter(new Date(d.date))}</br>${tooltipLabel}: ${commaFormatter(d.count)}`);
        }

        // animate area below line
        chartWrapper.select('.visualization__area')
            .datum(data)
            .transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attr('d', chartArea);

        // animate line
        path.datum(data)
            .transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attr('d', chartLine);

        // animate circles into position
        circles.data(data)
            .transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attr('cy', d => yScale(d.count));
    }

    function resize() {
        updateDimensions();

        // resize x axis
        svg.select('.x.axis')
            .attr('transform', `translate(0,${height})`)
            .call(xAxis)
            .selectAll('text')
            .attr('dy', '1em');

        // resize y axis
        svg.select('.y.axis')
            .call(yAxis);

        // Update the visualization area
        chartWrapper.select('.visualization__area')
            .attr('d', chartArea);

        // Update the chart line
        path.attr('d', chartLine);

        // Update positions of circles
        circles.attr('cx', d => xScale(d.date))
            .attr('cy', d => yScale(d.count));
    }

    function updateDimensions() {
        // Determine whether client is mobile width
        const mobile = window.innerWidth < breakPoint;

        // Set dimensions based on container size
        const containerWidth = container.node().getBoundingClientRect().width;

        // Measure yAxis label container
        let ylabelWidth = 0;
        const tickPadding = yAxis.tickPadding();
        chartWrapper.select('.y.axis').selectAll('text').each(function () {
            const currLabelWidth = this.getBBox().width + tickPadding;
            if (currLabelWidth > ylabelWidth) ylabelWidth = currLabelWidth;
        });

        // Measure xAxis labels to see if we need to widen yAxis area
        let xLabelWidth = 0;
        chartWrapper.select('.x.axis').selectAll('text').each(function () {
            const currLabelWidth = this.getBBox().width;
            if (currLabelWidth / 2 > xLabelWidth) xLabelWidth = currLabelWidth / 2;
        });

        if (xLabelWidth > ylabelWidth) ylabelWidth = xLabelWidth;

        // adjust margins if we're on a mobile device without mutating original margins
        marginRight = mobile ? 0 : margin.right + xLabelWidth;
        marginLeft = mobile ? 0 : margin.left + ylabelWidth;

        // adjust for margins
        width = containerWidth - marginLeft - marginRight;
        height = heightRatio * width;

        // update x and y scales to new dimensions
        xScale.range([0, width]);
        yScale.range([height, 0]);

        touchScale.___domain([ylabelWidth, width]).range([0, data.length - 1]).clamp(true);

        // update svg and wrapper to new dimensions
        svg.attr('width', width + marginLeft + marginRight)
            .attr('height', height + marginTop + marginBottom);
        chartWrapper.attr('transform', `translate(${marginLeft},${marginTop})`);

        // Update y axis tick format for mobile
        if (mobile) {
            yTickFormatter = format('.0s');
            xAxis = getAxis(xAxisOrientMobile, xScale);
            yAxis = getAxis(yAxisOrientMobile, yScale);
        } else {
            yTickFormatter = format(',d');
            xAxis = getAxis(xAxisOrient, xScale);
            yAxis = getAxis(yAxisOrient, yScale);
        }

        // update x and y axes
        xAxis.ticks(xAxisTickInterval.every(mobile ? xAxisTickStep * 2 : xAxisTickStep))
            .tickSizeOuter(0)
            .tickSizeInner(-height)
            .tickFormat(xAxisDateFormatter);

        yAxis.tickSizeOuter(0)
            .tickSizeInner(-width)
            .tickFormat(yTickFormatter);

        // Set area to new dimensions
        chartArea.x(d => xScale(d.date))
            .y0(height)
            .y1(d => yScale(d.count));

        chartLine.x(d => xScale(d.date))
            .y(d => yScale(d.count));
    }

    function onTouchMove() {
        const xPos = touches(this)[0][0];
        const d = data[~~touchScale(xPos)];

        circles.filter(function (cir) {
            if (cir.date === d.date) {
                $(this).trigger('mouseover');
            } else {
                $(this).trigger('mouseout');
            }
        });
    }

    // Return render function and container node (instead of container d3 object)
    return {
        render,
        animate,
        resize,
        container: container.node(),
    };
}

/**
 * Creates bar chart visualization with data source and options arguments.
 * @param {Object} dataSrc - Object of data to use for chart
 * @param {array} [dataSrc.allData] - Array of monthly data for items added.
 *      [ { year: {number}, month: {number[1-12]}, count: {number} }, ...]
 *
 * @param {number} breakPoint - Point at which the chart switches to mobile.
 * @param {string} containerSelector - Will be loaded into D3 selection.
 * @param {number} heightRatio - Ratio of height to width
 * @param {Object} margin - Specifies chart margins
 * @param {boolean} shiftEmpties - Shift data array to remove empty values at beginning
 * @param {string} statsTable - ID of stats table to associate with chart
 * @param {string} tickDateFormat - Date format for ticks
 * @param {string} tooltipDateFormat - Format of date in tooltip
 * @param {string} tooltipLabel - Descriptor of y axis value of chart in tooltip (e.g.: 'Usage: 13')
 * @param {boolean} tooltips - Whether to add tooltips
 * @param {number} transitionDelay - Delay of transitions in milliseconds
 * @param {number} transitionDuration - Duration of transitions in milliseconds
 * @param {number} xAxisBarPadding - Percentage of chart width between bars
 * @param {string} xAxisLabel - Label for x axis
 * @param {string} xAxisOrient - Orientation of x axis. ('top' 'bottom' 'left' 'right')
 * @param {string} xAxisOrientMobile - Orientation of x axis in mobile.
 *      ('top' 'bottom' 'left' 'right')
 * @param {Object} xAxisTickIntervalString - Time interval string
 * @param {number} xAxisTickStep - Defines steps between x axis ticks
 * @param {string} yAxisLabel - Label for y axis
 * @param {string} yAxisOrient - Orientation of y axis. ('top' 'bottom' 'left' 'right')
 * @param {string} yAxisOrientMobile - Orientation of y axis in mobile.
 *      ('top' 'bottom' 'left' 'right')
 * @param {number} yAxisTickCount - Approximate number of y axis ticks
 */
function StackedBarChart(dataSrc, {
    breakPoint = 768,
    containerSelector = 'body',
    heightRatio = 0.5,
    margin = { top: 10, right: 0, bottom: 50, left: 0 },
    shiftEmpties = false,
    statsTable = undefined,
    tickDateFormat = '%Y',
    tooltipDateFormat = '%b %d',
    tooltipLabel = 'items added',
    tooltips = true,
    transitionDelay = 500,
    transitionDuration = 1500,
    xAxisBarPadding = 4,
    xAxisLabel = undefined,
    xAxisOrient = 'bottom',
    xAxisOrientMobile = 'bottom',
    xAxisTickIntervalString = 'year',
    xAxisTickStep = 1,
    yAxisLabel = undefined,
    yAxisOrient = 'left',
    yAxisOrientMobile = 'right',
    yAxisTickCount = 5,
} = {}) {
    const commaFormatter = format(',');
    const container = select(containerSelector);
    const sourceData = dataSrc.allData;
    const marginBottom = margin.bottom;
    const marginTop = margin.top;
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    const tickDateFormatter = timeFormat(tickDateFormat);
    const tooltipDateFormatter = timeFormat(tooltipDateFormat);
    const xAxisTickInterval = getTimeInterval(xAxisTickIntervalString);
    const yearParse = timeParse('%Y');
    let barWidth = 50;
    let chartWrapper;
    let height;
    let marginLeft = margin.left;
    let marginRight = margin.right;
    let month;
    let svg;
    let width = parseInt(container.style('width'), 10)
        - parseInt(container.style('padding-left'), 10)
        - parseInt(container.style('padding-right'), 10)
        || document.documentElement.clientWidth;
    let xAxis;
    let xScale;
    let yAxis;
    let yScale;
    let year;

    // return early if no data is available
    if (dataSrc.allData === undefined) return {};

    // Group the data points by year
    // {
    //   key = {year as string},
    //   values = [ { count: {int}, month: {int} }, ... ]
    // }
    const data = nest()
        .key(d => d.year)
        .entries(sourceData);

    // Update data for chart.
    // {
    //   key = {string},
    //   values = [{
    //      count: {int},
    //      month: {string},
    //      y0: {int}
    //      y1: {int}
    //      year: {string}
    //
    data.forEach((d) => {
        let y0 = 0;
        d.values.forEach((element) => {
            element.y0 = y0;
            element.y1 = y0 += element.count;
            element.month = months[element.month - 1];
            element.year = element.year.toString();
        });
        d.total = d.values[d.values.length - 1].y1;
    });

    // Remove empty values at start of data
    if (shiftEmpties) {
        while (data[0].total === 0) {
            data.shift();
        }
    }

    init();

    function init() {
        // initialize scales and set domains
        xScale = scaleTime().nice()
            .___domain(extent(data, d => yearParse(d.key)));

        yScale = scaleLinear()
            .___domain([0, max(data, d => d.total)]);

        // initialize axes
        xAxis = getAxis(xAxisOrient, xScale);

        yAxis = getAxis(yAxisOrient, yScale)
            .ticks(yAxisTickCount)
            .tickFormat(format(',d'));

        // initialize svg elements
        svg = container.append('svg')
            .classed('visualization__outer-svg', true);
        chartWrapper = svg
            .append('g')
            .classed('visualization__inner-g', true);
        year = chartWrapper.selectAll('.visualization__bar');
        month = year.selectAll('.visualization__bar-layer');
        chartWrapper.append('g')
            .classed('x axis', true);
        chartWrapper.append('g')
            .classed('y axis', true);

        // render the chart
        render();

        // add event listeners to resize chart when window or nav is resized
        window.addEventListener('nav-resize', resize);
        window.addEventListener('resize', resize);
    }

    function render() {
        // call y axis before update dimensions to find max label length
        svg.select('.y.axis')
            .call(yAxis);

        // Set dimensions based on container size
        updateDimensions();

        // redraw y axis with new dimensions
        svg.select('.y.axis')
            .call(yAxis);

        // add x axis
        svg.select('.x.axis')
            .attr('transform', `translate(0,${height})`)
            .call(xAxis)
            .selectAll('text')
            .style('text-anchor', 'middle')
            .attr('dx', '0')
            .attr('dy', '1em');

        // add x axis label
        if (xAxisLabel) {
            chartWrapper.append('text')
                .classed('x label', true)
                .attr('text-anchor', 'end')
                .attr('x', width / 2)
                .attr('y', (height + marginBottom) - 30)
                .attr('dy', '.71em')
                .text(xAxisLabel);
        }

        // add y axis label
        chartWrapper.append('text')
            .classed('y label', true)
            .attr('transform', 'rotate(-90)')
            .attr('y', '5')
            .attr('x', '0')
            .attr('dy', '.71em')
            .style('text-anchor', 'end')
            .text(yAxisLabel);

        drawBars();

        // highlight value in table on hover
        $(container.selectAll('rect').nodes()).on('mouseover', function () {
            const $month = $(this).attr('data-month');
            const $year = $(this).attr('data-year');
            $(statsTable).find(`[data-year="${$year}"][data-month="${$month}"]`)
                .addClass('success');
        }).on('mouseout', function () {
            const $month = $(this).attr('data-month');
            const $year = $(this).attr('data-year');
            $(statsTable).find(`[data-year="${$year}"][data-month="${$month}"]`)
                .removeClass('success');
        });
    }

    function animate() {
        year.selectAll('.visualization__bar-layer')
            .transition()
            .duration(transitionDuration)
            .delay(transitionDelay)
            .attr('y', d => yScale(d.y1))
            .attr('height', d => yScale(d.y0) - yScale(d.y1));
    }

    function resize() {
        updateDimensions();

        // resize y axis
        svg.select('.y.axis')
            .call(yAxis);

        // resize x axis
        svg.select('.x.axis')
            .attr('transform', `translate(0,${height})`)
            .call(xAxis)
            .selectAll('text')
            .style('text-anchor', 'middle')
            .attr('dx', '0')
            .attr('dy', '1em');

        // resize year bars
        year.attr('transform', d => `translate(${xScale(yearParse(d.key))},0)`);

        // resize and position month bar fragments
        year.selectAll('rect')
            .attr('x', -(barWidth / 2))
            .attr('width', barWidth)
            .attr('y', d => yScale(d.y1))
            .attr('height', d => yScale(d.y0) - yScale(d.y1));
    }

    function drawBars() {
        // add g element for each year's bar
        year = year.data(data)
            .enter()
            .append('g')
            .attr('class', d => `visualization__bar  visualization__bar--${d.key}`)
            .attr('transform', d => `translate(${xScale(yearParse(d.key))},0)`);

        // add rectangles for each month inside corresponding year
        year.selectAll('rect')
            .data(d => d.values)
            .enter()
            .append('rect')
            .attr('data-year', d => d.year)
            .attr('data-month', d => months.indexOf(d.month) + 1)
            .attr('class', d => `visualization__bar-layer  visualization__bar-layer--${d.month.toLowerCase()}`)
            .attr('x', -(barWidth / 2))
            .attr('width', barWidth)
            .attr('y', height)
            .attr('height', 0);

        if (tooltips) {
            year.selectAll('.visualization__bar-layer')
                .attr('data-html', 'true')
                .attr('data-toggle', 'touch-tooltip')
                .attr('data-placement', 'top')
                .attr('title', d => `${d.month}</br>${commaFormatter(d.count)} ${tooltipLabel}`);
        }
    }

    function updateDimensions() {
        // Determine whether client is mobile width
        const mobile = window.innerWidth < breakPoint;

        // Set dimensions based on container size
        const containerWidth = container.node().getBoundingClientRect().width;

        // Measure yAxis label container
        let labelWidth = 0;
        chartWrapper.select('.y.axis').selectAll('text').each(function () {
            if (this.getBBox().width > labelWidth) labelWidth = this.getBBox().width;
        });

        // adjust margins if we're on a mobile device without mutating original margins
        marginRight = mobile ? 0 : margin.right;
        // Adding 3px to the left margin prevents the inner element from being larger than outer svg
        marginLeft = mobile ? 0 : margin.left + labelWidth + 3;

        // adjust for margins
        width = containerWidth - marginLeft - marginRight;
        height = heightRatio * width;

        // Calculate bar padding in pixels
        const barPadding = (width / 100) * xAxisBarPadding;
        // Determine bar width, add extra half bar because of horizontal centering of bars
        barWidth = (width - barPadding) / (data.length + 0.5);

        // update x and y scales to new dimensions, half bar for centering bars
        xScale.range([barWidth / 2, width - (barWidth / 2)]);
        yScale.range([height, 0]);

        // update svg and wrapper to new dimensions
        svg.attr('width', width + marginLeft + marginRight)
            .attr('height', height + marginTop + marginBottom);
        chartWrapper.attr('transform', `translate(${marginLeft},${marginTop})`);

        // update x and y axes scales
        xAxis = mobile ? getAxis(xAxisOrientMobile, xScale) : getAxis(xAxisOrient, xScale);
        xAxis.ticks(xAxisTickInterval.every(mobile ? xAxisTickStep * 2 : xAxisTickStep))
            .tickSizeOuter(0)
            .tickSizeInner(0);

        yAxis = mobile ? getAxis(yAxisOrientMobile, yScale) : getAxis(yAxisOrient, yScale);
        yAxis.tickSizeOuter(0)
            .tickSizeInner(-width);
    }

    // Return render function and container node (instead of container d3 object)
    return {
        render,
        animate,
        resize,
        container: container.node(),
    };
}

export {
    bindResize,
    renderInView,
    PieChart,
    BarChart,
    LineChart,
    StackedBarChart,
};
